import React from 'react';

import { Stack, Stepper, Text } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import dayjs from 'dayjs';

import WizardFooter from './WizardFooter';
import WizardStep, { WizardStepProps } from './WizardStep';
import { useChangeModal } from '../../ChangeModalContext';

interface WizardComposition {
  Step: typeof WizardStep;
}

interface WizardProps {
  children: React.ReactNode;
}

const Wizard: React.FC<WizardProps> & WizardComposition = ({ children }) => {
  const { currentStep, visibleSteps, athletePackage } = useChangeModal();

  const existingChild = athletePackage.child;
  const existingChildKind = existingChild?.isChange ? 'scheduled change' : 'renewal';

  const stepNodes = React.Children.toArray(children).filter(
    (node): node is React.ReactElement<WizardStepProps> =>
      React.isValidElement(node) && node.type === WizardStep,
  );

  const activeNode = stepNodes.find(node => node.props.name === currentStep && !node.props.skip);

  const activeIndex = Math.max(0, visibleSteps.indexOf(currentStep));

  return (
    <Stack spacing='md'>
      <Stepper active={activeIndex} size='sm' breakpoint='sm' allowNextStepsSelect={false}>
        {visibleSteps.map(step => (
          <Stepper.Step key={step} label={step} />
        ))}
      </Stepper>
      {existingChild && currentStep !== 'Review' && (
        <KiloAlert variantStyle='warning'>
          <Text size='sm'>
            This package already has a {existingChildKind} ({existingChild.packageName}, starts{' '}
            {dayjs(existingChild.startDate).format('MMM D, YYYY')}). Completing this wizard will replace it.
          </Text>
        </KiloAlert>
      )}
      {activeNode}
      <WizardFooter />
    </Stack>
  );
};

Wizard.Step = WizardStep;

export default Wizard;
